export interface ProductProcessingUsage {
  completed: number;
  trackingStartedAt: string;
}

/** A real UTC calendar date in YYYY-MM-DD form, e.g. a usage period boundary. */
export function isUtcCalendarDate(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(value);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function parseProductProcessingUsage(value: unknown): ProductProcessingUsage | undefined {
  if (!value || typeof value !== "object") return undefined;
  const data = value as Partial<ProductProcessingUsage>;
  if (typeof data.completed !== "number" || !Number.isSafeInteger(data.completed) || data.completed < 0 ||
      typeof data.trackingStartedAt !== "string") return undefined;
  // Require an explicit timezone, then normalize offsets and optional fractional
  // seconds before the UI compares UTC calendar dates. Every time and offset field is
  // range-checked here, so rejection never depends on how an engine parses or rolls
  // over values; hour 24 in particular would read as midnight of the next day.
  if (!/^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+)?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/.test(data.trackingStartedAt)) return undefined;
  // Normalize to the three millisecond digits supported consistently by Date.
  const timestamp = data.trackingStartedAt.replace(/\.(\d+)(?=Z|[+-])/, (_match, fraction: string) =>
    `.${fraction.padEnd(3, "0").slice(0, 3)}`);
  const start = new Date(timestamp);
  if (Number.isNaN(start.getTime())) return undefined;
  // Date accepts rollover dates such as February 30; reject those explicitly.
  const day = data.trackingStartedAt.slice(0, 10);
  const calendarDate = new Date(`${day}T00:00:00.000Z`);
  if (Number.isNaN(calendarDate.getTime()) || calendarDate.toISOString().slice(0, 10) !== day) return undefined;
  return { completed: data.completed, trackingStartedAt: start.toISOString() };
}
